import axios from 'axios';
import type { Paginated } from '@relay-towing/api';
import env from '../config/env';
import { SELECTED_COMPANY_KEY } from '../hooks/userCompanies.shared';

export type InspectionStatus = 'IN_PROGRESS' | 'PASSED' | 'ISSUES_FOUND';
export type AnswerType =
  | 'PASS_FAIL'
  | 'NUMBER'
  | 'TEXT'
  | 'SELECT'
  | 'CHECKBOX'
  | 'YES_NO'
  | 'PHOTO';
export type AnswerStatus = 'PASS' | 'FAIL';
export type PhotoRule = 'NONE' | 'ON_FAIL' | 'ALWAYS';
export type DefectSeverity = 'MINOR' | 'MAJOR' | 'OUT_OF_SERVICE';
export type InspectionCovers = 'TRUCK' | 'EQUIPMENT' | 'ANY';
export type InspectionSubjectType = 'TRUCK' | 'EQUIPMENT';

/** One row on the home screen. Not the full checklist; no questions here. */
export interface ChecklistRow {
  id: string;
  name: string;
  description?: string | null;
  covers: InspectionCovers;
  itemCount: number;
  /** The newest report this driver filed on this checklist, if any. */
  lastRun: {
    id: string;
    submittedAt: string | null;
    subjectLabel: string;
  } | null;
}

/** The last time anybody looked at a truck, and who it was. */
export interface LastCheck {
  id: string;
  status: InspectionStatus;
  submittedAt: string | null;
  by: string | null;
}

export interface TruckOption {
  id: string;
  truckNumber?: string | null;
  licensePlate?: string | null;
  make?: string | null;
  model?: string | null;
  truckType?: string | null;
  status: string;
  isAssignedToMe: boolean;
  openDefectCount: number;
  isOutOfService: boolean;
  lastCheck: LastCheck | null;
}

export interface ChecklistItem {
  id: string;
  section?: string | null;
  label: string;
  helpText?: string | null;
  answerType: AnswerType;
  options: string[];
  unit?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  isRequired: boolean;
  photoRule: PhotoRule;
  affectsStatus: boolean;
  sortOrder: number;
}

export interface Checklist {
  id: string;
  name: string;
  description?: string | null;
  requireSignature: boolean;
  items: ChecklistItem[];
}

export interface OpenDefect {
  id: string;
  label: string;
  note?: string | null;
  severity: DefectSeverity;
  reportedAt: string;
  photoUrls: string[];
}

export interface StartInspectionResponse {
  template: Checklist;
  /** What this run is being done on, named the way the screens show it. */
  subject: {
    type: InspectionSubjectType;
    label: string;
    truckId: string | null;
  };
  /** Null when the run is on a piece of equipment rather than a truck. */
  truck: TruckOption | null;
  driverId: string | null;
  /**
   * The last check filed on this truck or piece of equipment, shown at the
   * top of the form.
   */
  lastInspection: {
    id: string;
    status: InspectionStatus;
    submittedAt?: string | null;
    odometer?: number | null;
    signedName?: string | null;
    driver?: { id: string; name: string } | null;
  } | null;
}

/** Which truck, or which piece of equipment. Exactly one of the two. */
export interface InspectionSubjectInput {
  truckId?: string;
  equipmentLabel?: string;
}

export type InspectionPhotoSource = 'CAMERA' | 'UPLOAD';

export interface SubmitPhoto {
  url: string;
  source: InspectionPhotoSource;
  takenAt?: string;
  latitude?: number;
  longitude?: number;
}

/** A photo's story minus its URL: where it came from, and what its EXIF said. */
export type PhotoMeta = Omit<SubmitPhoto, 'url'>;

/** What POST /inspections/upload answers: the URL, and the EXIF if there was any. */
export interface UploadedPhoto {
  url: string;
  takenAt?: string;
  latitude?: number;
  longitude?: number;
}

export interface SubmitAnswer {
  itemId?: string;
  itemLabel: string;
  itemSection?: string;
  itemType: AnswerType;
  status?: AnswerStatus;
  valueNumber?: number;
  valueText?: string;
  /** CHECKBOX only: the options that were ticked. */
  valueList?: string[];
  note?: string;
  photos?: SubmitPhoto[];
}

export interface SubmitInspectionBody {
  templateId: string;
  truckId?: string;
  equipmentLabel?: string;
  answers: SubmitAnswer[];
  odometer?: number;
  notes?: string;
  latitude?: number;
  longitude?: number;
  locationLabel?: string;
  signatureUrl?: string;
  signedName?: string;
  /** Photos of the subject itself rather than of any one question. */
  photos?: SubmitPhoto[];
  /**
   * Made once when the checklist opens and sent with every attempt. A submit
   * that times out gets tapped again, and this is what lets the server return
   * the report it already filed instead of filing a second one.
   */
  clientSubmissionId?: string;
}

export interface ReportAnswer {
  id: string;
  itemLabel: string;
  itemSection?: string | null;
  itemType: AnswerType;
  itemUnit?: string | null;
  status?: AnswerStatus | null;
  valueNumber?: number | null;
  valueText?: string | null;
  /** CHECKBOX only: the options that were ticked. */
  valueList?: string[];
  note?: string | null;
  photoUrls: string[];
}

export interface InspectionReport {
  id: string;
  /** Null when the check was on a piece of equipment rather than a truck. */
  truckId: string | null;
  status: InspectionStatus;
  odometer?: number | null;
  submittedAt?: string | null;
  templateName: string;
  signedName?: string | null;
  signatureUrl?: string | null;
  notes?: string | null;
  /** Photos of the truck itself rather than of any one question. */
  photoUrls?: string[] | null;
  /** Where the check was done. The label is what the report shows. */
  locationLabel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  answers: ReportAnswer[];
  subjectType: InspectionSubjectType;
  /** What was checked, named when the report was filed. Never empty. */
  subjectLabel: string;
}

export interface MyInspection {
  id: string;
  status: InspectionStatus;
  submittedAt?: string | null;
  odometer?: number | null;
  templateName: string;
  subjectType: InspectionSubjectType;
  subjectLabel: string;
  truck: {
    id: string;
    truckNumber?: string | null;
    licensePlate?: string | null;
  } | null;
  _count: { defects: number };
}

/**
 * This feature's own axios instance rather than driverApi's.
 *
 * driverApi keeps its client private and predates Prettier in this repo, so
 * adding an accessor rewrites eight hundred lines of it in the diff. The parts
 * that matter here are two headers, and the hooks in useTruckInspections
 * already fetch a fresh Auth0 token before every call, so none of driverApi's
 * 401-refresh machinery is load bearing for these endpoints.
 */
let authToken: string | null = null;
let companyId: string | null = null;

/**
 * Called by the hooks before each request.
 *
 * The company has to be passed in. Reading it from localStorage the way
 * driverApi's interceptor does only works for someone who has actively switched
 * company; a driver who belongs to exactly one never writes that key, so the
 * header went out empty and every request 400d.
 */
export const setInspectionsContext = (token: string, company?: string) => {
  authToken = token;
  // Assign either way. This used to only write a truthy value, which made it
  // write-once: after a company switch the old id stayed and every request went
  // to the wrong yard.
  companyId = company ?? null;
};

const client = axios.create({
  baseURL: env.apiUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  const company = companyId || localStorage.getItem(SELECTED_COMPANY_KEY);
  if (company) config.headers['x-company-id'] = company;
  return config;
});

export const truckInspectionsApi = {
  async listTrucks(): Promise<TruckOption[]> {
    const { data } = await client.get('/inspections/trucks');
    return data;
  },

  async listChecklists(): Promise<ChecklistRow[]> {
    const { data } = await client.get('/inspections/checklists');
    return data;
  },

  async start(
    templateId: string,
    subject: InspectionSubjectInput
  ): Promise<StartInspectionResponse> {
    const { data } = await client.post('/inspections/start', {
      templateId,
      ...subject,
    });
    return data;
  },

  /**
   * Longer than the fifteen seconds everything else gets.
   *
   * This is the one request that must not be abandoned: the driver has already
   * walked the truck. Writing a report is a transaction with a row per answer,
   * and on a slow link to the database it has taken over twenty seconds, at
   * which point the phone gave up on a submit the server went on to save. The
   * driver saw "could not send" for a report the office could already read.
   */
  async submit(body: SubmitInspectionBody) {
    const { data } = await client.post('/inspections/submit', body, {
      timeout: 60000,
    });
    return data;
  },

  /** One finished report. The token is passed in so the caller owns the refresh. */
  async get(
    token: string,
    id: string,
    company?: string
  ): Promise<InspectionReport> {
    setInspectionsContext(token, company);
    const { data } = await client.get(`/inspections/${id}`);
    return data;
  },

  /** The reports the caller filed, newest first: the shared list, filtered. */
  async mine(limit = 20): Promise<MyInspection[]> {
    const { data } = await client.get('/inspections', {
      params: { mine: 'true', pageSize: limit },
    });
    return (data as Paginated<MyInspection>).data;
  },

  /** Uploads one photo and returns its URL with whatever EXIF it carried. */
  async uploadPhoto(file: File): Promise<UploadedPhoto> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await client.post('/inspections/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return data;
  },
};
